import React, { memo } from "react";
import { Clock3 } from "lucide-react";
import { hhmmToMinutes, words } from "./fmt";

/*
 * SESSION TIMELINE — where in the trading day we are, drawn from the server's
 * own session windows (fetchBuyerEdge().session). Nothing here is hard-coded:
 * the opening-noise end, entry cutoff and square-off time all come from the
 * same sys settings the autotrader trades on, so the strip can never disagree
 * with the engine.
 *
 * The cursor is driven by the page's existing minute clock (`minuteOfDay`);
 * this component owns no timer of its own.
 *
 * The server's windows overlap (opening range sits inside opening noise plus
 * the entry window; midday theta sits inside the entry window), so they are
 * drawn in two lanes: the primary lane carries avoid / trade / exit, the
 * overlay lane carries the context / caution annotations.
 */

const KIND = {
  avoid: { bar: "bg-rose-500/70", text: "text-rose-300", dot: "bg-rose-400" },
  exit: { bar: "bg-rose-600/80", text: "text-rose-300", dot: "bg-rose-500" },
  trade: { bar: "bg-emerald-500/60", text: "text-emerald-300", dot: "bg-emerald-400" },
  caution: { bar: "bg-amber-400/60", text: "text-amber-300", dot: "bg-amber-400" },
  context: { bar: "bg-slate-500/50", text: "text-slate-400", dot: "bg-slate-400" },
};
const PRIMARY = new Set(["avoid", "trade", "exit"]);

const PHASE_TONE = {
  PRE_OPEN: "text-slate-400", OPENING_NOISE: "text-rose-300", ENTRY_WINDOW: "text-emerald-300",
  MIDDAY_THETA: "text-amber-300", NO_NEW_ENTRIES: "text-rose-300", SQUARE_OFF: "text-rose-300",
  CLOSED: "text-slate-400",
};

function minutesLeft(target, now) {
  if (target == null || now == null) return null;
  return target - now;
}

function Lane({ windows, open, span, tall }) {
  return (
    <div className={`relative w-full overflow-hidden rounded-full bg-slate-800 ${tall ? "h-4" : "h-1.5"}`}>
      {windows.map((w) => {
        const s = hhmmToMinutes(w.start);
        const e = hhmmToMinutes(w.end);
        if (s == null || e == null || e <= s) return null;
        const left = ((s - open) / span) * 100;
        const width = ((e - s) / span) * 100;
        const k = KIND[w.kind] || KIND.context;
        return (
          <div key={w.key} title={`${w.label} · ${w.start}–${w.end}`}
            className={`absolute top-0 h-full ${k.bar}`} style={{ left: `${left}%`, width: `${width}%` }}>
            {tall && width > 12 && (
              <span className="hidden truncate px-1.5 text-[9px] font-bold leading-4 text-on-dark sm:block">{w.label}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ``children`` renders as a footer row inside the same card — the dashboard puts
 * the data-freshness chips there so "session" and "data" are one status band. */
function SessionTimeline({ session, minuteOfDay, children }) {
  if (!session) {
    return (
      <section data-testid="session-timeline" className="rounded-2xl border border-line bg-surface px-3 py-2 text-[10px] text-slate-500">
        <Clock3 className="mr-1.5 inline h-3.5 w-3.5" />Session timeline appears when the buyer-edge feed answers.
        {children && <div className="mt-2 border-t border-line pt-2">{children}</div>}
      </section>
    );
  }
  const open = hhmmToMinutes(session.market_open) ?? 9 * 60 + 15;
  const close = hhmmToMinutes(session.market_close) ?? 15 * 60 + 30;
  const span = Math.max(1, close - open);
  const windows = session.windows || [];
  const primary = windows.filter((w) => PRIMARY.has(w.kind));
  const overlay = windows.filter((w) => !PRIMARY.has(w.kind));
  const now = minuteOfDay ?? hhmmToMinutes(session.now);
  const inSession = now != null && now >= open && now <= close;
  const cursor = now == null ? null : Math.max(0, Math.min(100, ((now - open) / span) * 100));
  const cutoff = hhmmToMinutes(session.entry_cutoff);
  const squareoff = hhmmToMinutes(session.eod_squareoff);
  // Prefer the live clock; the server's numbers are up to a poll old.
  const toCutoff = inSession ? minutesLeft(cutoff, now) : session.minutes_to_cutoff;
  const toSquare = inSession ? minutesLeft(squareoff, now) : session.minutes_to_squareoff;
  const phase = session.phase || "—";

  const markers = [
    { at: cutoff, label: "cutoff" },
    { at: squareoff, label: "square-off" },
  ].filter((m) => m.at != null);

  return (
    <section data-testid="session-timeline" className="rounded-2xl border border-line bg-surface px-3 py-2.5">
      <div className="relative pt-1">
        <Lane windows={primary} open={open} span={span} tall />
        <div className="mt-1"><Lane windows={overlay} open={open} span={span} /></div>
        {markers.map((m) => (
          <span key={m.label} title={`${m.label} ${String(session[m.label === "cutoff" ? "entry_cutoff" : "eod_squareoff"])}`}
            className="absolute top-0 h-[30px] w-px bg-slate-300/70"
            style={{ left: `${((m.at - open) / span) * 100}%` }} />
        ))}
        {cursor != null && (
          <span data-testid="session-cursor" title={`now ${session.now || ""}`}
            className={`absolute -top-0.5 h-[34px] w-0.5 rounded-full ${inSession ? "bg-cyan-300 shadow-[0_0_8px_rgb(var(--c-cyan-400)/.9)]" : "bg-slate-500"}`}
            style={{ left: `calc(${cursor}% - 1px)` }} />
        )}
      </div>
      <div className="mt-1 flex justify-between text-[9px] tabular-nums text-slate-500">
        <span>{session.market_open || "—"}</span>
        <span>{session.entry_cutoff ? `cutoff ${session.entry_cutoff}` : ""}</span>
        <span>{session.eod_squareoff ? `sq-off ${session.eod_squareoff}` : ""}</span>
        <span>{session.market_close || "—"}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
        <span className={`font-black uppercase tracking-wider ${PHASE_TONE[phase] || "text-slate-300"}`}>{words(phase)}</span>
        <span className="text-slate-600">·</span>
        <span className="tabular-nums text-slate-400">
          {toCutoff == null ? "—" : toCutoff > 0 ? `${toCutoff} min to entry cutoff` : "entry cutoff passed"}
        </span>
        <span className="text-slate-600">·</span>
        <span className="tabular-nums text-slate-400">
          {toSquare == null ? "—" : toSquare > 0 ? `${toSquare} min to square-off` : "square-off due"}
        </span>
        <span className="ml-auto flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] text-slate-500 sm:hidden">
          {windows.map((w) => (
            <span key={w.key} className="inline-flex items-center gap-1">
              <span className={`h-1.5 w-1.5 rounded-full ${(KIND[w.kind] || KIND.context).dot}`} />{w.label} {w.start}–{w.end}
            </span>
          ))}
        </span>
      </div>
      {children && <div className="mt-2 border-t border-line pt-2">{children}</div>}
    </section>
  );
}

export default memo(SessionTimeline);
